import React from 'react';
import { CONTACT } from '@/utils/format';
import { Link } from 'react-router-dom';
import { FileText, ShieldCheck, ScrollText, Truck, RotateCcw, Mail } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Shared layout
// ─────────────────────────────────────────────────────────────────
const LegalShell = ({ eyebrow, title, updated, icon: Icon, children, testid }) => (
  <div className="max-w-3xl mx-auto px-4 lg:px-8 py-12" data-testid={testid}>
    <div className="mb-8">
      <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-3">
        <Icon className="w-4 h-4" /> {eyebrow}
      </div>
      <h1 className="font-display font-bold text-4xl sm:text-5xl text-[#1C1917] tracking-tight leading-tight">
        {title}
      </h1>
      <p className="text-xs text-[#8BA58F] uppercase tracking-widest mt-3">
        Última actualización: {updated}
      </p>
    </div>

    <div className="bg-white rounded-3xl border border-[#E7E5E4] p-6 sm:p-10 legal-doc">
      {children}
    </div>

    <div className="mt-10 rounded-2xl bg-[#F7F2E8] border border-[#E7E5E4] p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
      <div>
        <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">¿Preguntas?</p>
        <p className="text-sm text-[#57534E]">
          Escríbenos y respondemos en menos de 24 horas hábiles.
        </p>
      </div>
      <Link
        to="/contacto"
        className="inline-flex items-center gap-2 bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-5 py-2.5 text-sm font-semibold"
      >
        <Mail className="w-4 h-4" /> Contactar
      </Link>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
// COMPANY INFO used across all documents
// ─────────────────────────────────────────────────────────────────
const COMPANY = {
  brand: 'Nutripet Gourmet',
  legal: 'Nutripet Gourmet S.A.S.',
  domain: 'nutripetgourmet.com.co',
  email: CONTACT.email,
  phone: CONTACT.phoneDisplay,
  address: 'Bogotá D.C., Colombia',
};

// ─────────────────────────────────────────────────────────────────
// 1) Términos y condiciones
// ─────────────────────────────────────────────────────────────────
export const Terms = () => (
  <LegalShell
    eyebrow="Legal"
    title="Términos y condiciones"
    updated="Febrero 2026"
    icon={ScrollText}
    testid="page-terms"
  >
    <h2>1. Aceptación</h2>
    <p>
      Al ingresar y usar el sitio web <b>{COMPANY.domain}</b> operado por{' '}
      <b>{COMPANY.legal}</b> (en adelante, {COMPANY.brand}), el usuario acepta los presentes
      Términos y Condiciones y las políticas asociadas (Privacidad, Tratamiento de Datos,
      Envíos y Devoluciones). Si no está de acuerdo, debe abstenerse de utilizar el sitio.
    </p>

    <h2>2. Objeto del sitio</h2>
    <p>
      {COMPANY.brand} comercializa alimento y productos para perros y gatos de marcas
      premium, en territorio colombiano. Los precios se expresan en <b>pesos colombianos
      (COP) con IVA incluido</b>. El costo de envío se calcula en el checkout según
      destino y peso.
    </p>

    <h2>3. Registro y cuenta</h2>
    <ul>
      <li>El usuario debe ser mayor de 18 años y proporcionar información veraz.</li>
      <li>Es responsable de mantener la confidencialidad de su contraseña.</li>
      <li>{COMPANY.brand} puede suspender cuentas que incumplan estos Términos.</li>
    </ul>

    <h2>4. Proceso de compra</h2>
    <ol>
      <li>El usuario agrega productos al carrito.</li>
      <li>En el checkout ingresa sus datos de contacto, facturación (DIAN) y dirección de envío.</li>
      <li>El pago se procesa mediante la pasarela <b>Bold</b> (tarjeta, PSE, Nequi). {COMPANY.brand} no almacena información financiera del comprador.</li>
      <li>Una vez confirmado el pago, se envía un correo con la confirmación del pedido.</li>
    </ol>

    <h2>5. Disponibilidad y precios</h2>
    <p>
      Los precios y stock pueden variar sin previo aviso. Si por error un producto se
      publica con un precio incorrecto, {COMPANY.brand} podrá cancelar la orden y
      reembolsar el 100% del valor pagado.
    </p>

    <h2>6. Cupones y promociones</h2>
    <ul>
      <li>Los cupones son personales, no acumulables, y de un solo uso salvo que se indique lo contrario.</li>
      <li>El cupón de bienvenida y el cupón de referido tienen vigencia limitada indicada en el correo de entrega.</li>
      <li>{COMPANY.brand} se reserva el derecho de invalidar cupones ante uso fraudulento.</li>
    </ul>

    <h2>7. Propiedad intelectual</h2>
    <p>
      Todo el contenido del sitio (marcas, logotipos, textos, imágenes, código) es
      propiedad de {COMPANY.legal} o de sus proveedores. Está prohibida su reproducción
      sin autorización escrita.
    </p>

    <h2>8. Limitación de responsabilidad</h2>
    <p>
      {COMPANY.brand} no es responsable por reacciones adversas de la mascota a un
      alimento específico. Se recomienda consultar al médico veterinario, especialmente
      para dietas especializadas o de prescripción.
    </p>

    <h2>9. Modificaciones</h2>
    <p>
      {COMPANY.brand} podrá modificar estos Términos en cualquier momento. Los cambios
      relevantes se comunicarán en el sitio y aplicarán a partir de su publicación.
    </p>

    <h2>10. Ley aplicable y jurisdicción</h2>
    <p>
      Los presentes Términos se rigen por la legislación colombiana. Cualquier disputa
      será resuelta ante los jueces competentes de Bogotá D.C.
    </p>

    <h2>11. Contacto</h2>
    <p>
      {COMPANY.legal} · {COMPANY.address} · {COMPANY.email} · {COMPANY.phone}
    </p>
  </LegalShell>
);

// ─────────────────────────────────────────────────────────────────
// 2) Política de privacidad
// ─────────────────────────────────────────────────────────────────
export const Privacy = () => (
  <LegalShell
    eyebrow="Legal"
    title="Política de privacidad"
    updated="Febrero 2026"
    icon={ShieldCheck}
    testid="page-privacy"
  >
    <h2>1. Responsable del tratamiento</h2>
    <p>
      {COMPANY.legal}, con domicilio en {COMPANY.address}, es la Responsable del Tratamiento
      de los datos personales recopilados a través de <b>{COMPANY.domain}</b>.
    </p>

    <h2>2. Datos que recopilamos</h2>
    <ul>
      <li><b>De contacto:</b> nombre, correo, teléfono.</li>
      <li><b>De facturación:</b> tipo y número de documento, razón social, régimen DIAN.</li>
      <li><b>De envío:</b> dirección, barrio, ciudad, departamento.</li>
      <li><b>De navegación:</b> cookies técnicas, IP y páginas visitadas (para analítica anónima).</li>
      <li><b>De compra:</b> historial de pedidos, cupones usados, reseñas y valoraciones.</li>
    </ul>
    <p><b>No almacenamos datos financieros</b> (tarjetas, PSE) — estos se procesan directamente en Bold.</p>

    <h2>3. Finalidad</h2>
    <ol>
      <li>Procesar pedidos, pagos, envíos y postventa.</li>
      <li>Enviar comunicaciones transaccionales (confirmaciones, guías, reseñas).</li>
      <li>Analítica interna y mejora del sitio.</li>
      <li>Marketing directo <b>opt-in</b> (ofertas, novedades) — puedes retirarte cuando quieras.</li>
      <li>Cumplimiento de obligaciones legales y contables ante la DIAN.</li>
    </ol>

    <h2>4. Compartición de datos</h2>
    <p>Compartimos datos únicamente con proveedores necesarios para operar:</p>
    <ul>
      <li><b>Bold</b> (pasarela de pagos)</li>
      <li><b>Resend</b> (envío de correos transaccionales)</li>
      <li><b>Twilio</b> (envío de mensajes de WhatsApp)</li>
      <li><b>Transportadora</b> asignada al envío (Servientrega, Coordinadora u otra)</li>
    </ul>
    <p>No vendemos, alquilamos ni cedemos datos a terceros con fines comerciales.</p>

    <h2>5. Conservación</h2>
    <p>
      Conservamos los datos por el tiempo necesario para las finalidades y por los plazos
      obligatorios de conservación fiscal y contable (mínimo 5 años).
    </p>

    <h2>6. Cookies</h2>
    <p>
      Usamos cookies estrictamente necesarias (sesión, carrito) y analíticas anónimas
      (PostHog). Puedes gestionar cookies desde la configuración de tu navegador.
    </p>

    <h2>7. Derechos del titular</h2>
    <p>
      De acuerdo con la Ley 1581 de 2012, puedes ejercer los derechos de conocer,
      actualizar, rectificar, suprimir o revocar la autorización sobre tus datos.
      Consulta la <Link to="/tratamiento-datos" className="text-[#2A5C43] underline">Política de Tratamiento de Datos</Link> para el detalle del procedimiento.
    </p>

    <h2>8. Seguridad</h2>
    <p>
      Aplicamos medidas técnicas y administrativas razonables: cifrado en tránsito
      (HTTPS), autenticación segura con JWT en cookies httpOnly, contraseñas hasheadas
      con bcrypt y minimización de datos financieros (nunca almacenados).
    </p>

    <h2>9. Contacto</h2>
    <p>Escribe a <b>{COMPANY.email}</b> para cualquier consulta sobre privacidad.</p>
  </LegalShell>
);

// ─────────────────────────────────────────────────────────────────
// 3) Tratamiento de Datos (Ley 1581 de 2012)
// ─────────────────────────────────────────────────────────────────
export const DataTreatment = () => (
  <LegalShell
    eyebrow="Ley 1581 de 2012 · Habeas data"
    title="Política de tratamiento de datos personales"
    updated="Febrero 2026"
    icon={FileText}
    testid="page-data-treatment"
  >
    <p>
      En cumplimiento de la <b>Ley 1581 de 2012</b>, del Decreto 1377 de 2013 y demás normas
      concordantes, {COMPANY.legal} adopta la presente Política de Tratamiento de Datos
      Personales (en adelante, la Política).
    </p>

    <h2>1. Responsable</h2>
    <ul>
      <li><b>Razón social:</b> {COMPANY.legal}</li>
      <li><b>Domicilio:</b> {COMPANY.address}</li>
      <li><b>Correo:</b> {COMPANY.email}</li>
      <li><b>Teléfono:</b> {COMPANY.phone}</li>
    </ul>

    <h2>2. Definiciones</h2>
    <ul>
      <li><b>Titular:</b> persona natural cuyos datos personales son objeto de tratamiento.</li>
      <li><b>Dato personal:</b> cualquier información vinculada a una persona natural determinada o determinable.</li>
      <li><b>Dato sensible:</b> aquellos que afectan la intimidad del titular (p. ej. salud, biometría). <b>{COMPANY.brand} no recopila datos sensibles</b>.</li>
      <li><b>Tratamiento:</b> cualquier operación sobre datos personales (recolección, uso, almacenamiento, supresión).</li>
    </ul>

    <h2>3. Principios rectores</h2>
    <p>
      Aplicamos los principios de legalidad, finalidad, libertad, veracidad, transparencia,
      acceso restringido, seguridad y confidencialidad establecidos en la Ley 1581.
    </p>

    <h2>4. Finalidades del tratamiento</h2>
    <ol>
      <li>Ejecución del contrato de compraventa: procesar pedidos, cobrar, entregar y facturar.</li>
      <li>Atención al cliente y soporte postventa.</li>
      <li>Envío de comunicaciones transaccionales.</li>
      <li>Envío de comunicaciones comerciales <b>previo consentimiento (opt-in)</b>.</li>
      <li>Cumplimiento de deberes legales, fiscales y contables.</li>
      <li>Prevención de fraude y auditoría interna.</li>
    </ol>

    <h2>5. Derechos del titular</h2>
    <p>Como titular de tus datos personales, tienes derecho a:</p>
    <ul>
      <li><b>Conocer</b> qué datos tuyos tratamos.</li>
      <li><b>Actualizar y rectificar</b> tus datos.</li>
      <li><b>Solicitar prueba</b> de la autorización otorgada.</li>
      <li><b>Ser informado</b> sobre el uso de tus datos.</li>
      <li><b>Presentar quejas</b> ante la Superintendencia de Industria y Comercio (SIC).</li>
      <li><b>Revocar la autorización</b> y/o <b>solicitar la supresión</b> del dato cuando no medie deber legal de conservación.</li>
      <li><b>Acceder gratuitamente</b> a tus datos que hayan sido objeto de tratamiento.</li>
    </ul>

    <h2>6. Procedimiento para ejercer derechos</h2>
    <ol>
      <li>Enviar solicitud escrita a <b>{COMPANY.email}</b> con el asunto <i>"Habeas data — {'{Consulta/Reclamo}'}"</i>.</li>
      <li>Incluir: nombre completo, número de documento, medios de contacto, hechos que motivan la solicitud y documentación de soporte.</li>
      <li>{COMPANY.brand} responderá <b>consultas</b> en máximo <b>10 días hábiles</b> y <b>reclamos</b> en máximo <b>15 días hábiles</b>, contados a partir del día siguiente a la recepción, prorrogables por 8 días hábiles adicionales cuando aplique.</li>
    </ol>

    <h2>7. Autorización</h2>
    <p>
      Al crear una cuenta, realizar una compra o suscribirte a comunicaciones, el titular
      <b> otorga autorización libre, previa e informada</b> para el tratamiento de sus datos
      conforme a las finalidades declaradas en esta Política. La autorización puede
      revocarse en cualquier momento, salvo cuando exista deber legal o contractual de
      conservar los datos.
    </p>

    <h2>8. Transferencia y transmisión</h2>
    <p>
      Los datos pueden transferirse/transmitirse a proveedores contratados por
      {COMPANY.brand} (procesadores de pago, servicios de correo, transportadoras),
      quienes están obligados a cumplir estándares equivalentes de protección.
    </p>

    <h2>9. Seguridad</h2>
    <p>
      Implementamos medidas físicas, técnicas y administrativas apropiadas: cifrado TLS,
      autenticación segura, contraseñas hasheadas, control de acceso por rol y
      minimización de datos financieros (nunca almacenados en nuestros sistemas).
    </p>

    <h2>10. Vigencia</h2>
    <p>
      La presente Política rige a partir de febrero de 2026 y estará vigente mientras
      {COMPANY.brand} desarrolle su objeto social. Los datos se conservarán por los
      plazos legales aplicables (mínimo 5 años en materia contable y fiscal).
    </p>
  </LegalShell>
);

// ─────────────────────────────────────────────────────────────────
// 4) Política de envíos
// ─────────────────────────────────────────────────────────────────
export const ShippingPolicy = () => (
  <LegalShell
    eyebrow="Envíos"
    title="Política de envíos"
    updated="Febrero 2026"
    icon={Truck}
    testid="page-shipping"
  >
    <h2>1. Cobertura</h2>
    <p>
      Realizamos envíos a <b>todo el territorio colombiano</b>. En Bogotá contamos con
      cobertura urbana propia; en las demás ciudades y municipios operamos a través
      de transportadoras aliadas.
    </p>

    <h2>2. Tiempos estimados</h2>
    <ul>
      <li><b>Bogotá:</b> 1 a 2 días hábiles.</li>
      <li><b>Ciudades principales:</b> 2 a 4 días hábiles.</li>
      <li><b>Municipios y zonas apartadas:</b> 4 a 7 días hábiles.</li>
    </ul>
    <p className="text-sm text-[#8BA58F]">
      Los tiempos comienzan a contar una vez confirmado el pago. Pedidos realizados
      después de las 3:00 p. m. se procesan el siguiente día hábil.
    </p>

    <h2>3. Costos</h2>
    <p>
      El costo de envío se calcula automáticamente en el checkout según:
    </p>
    <ul>
      <li>Ciudad y departamento de destino.</li>
      <li>Peso total del pedido (en kg).</li>
    </ul>
    <p>
      Ofrecemos <b>envío gratis</b> a partir de un monto mínimo que se muestra en el
      checkout una vez seleccionas tu dirección.
    </p>

    <h2>4. Cambios de dirección</h2>
    <p>
      Puedes solicitar un cambio de dirección <b>antes de que el pedido pase a estado
      "En camino"</b>. Escríbenos a <b>{COMPANY.email}</b> o por WhatsApp con tu número
      de pedido.
    </p>

    <h2>5. Rastreo</h2>
    <p>
      Cuando tu pedido salga en ruta te enviaremos un correo con el <b>número de guía</b>
      y un enlace público de rastreo en <Link to="/rastreo" className="text-[#2A5C43] underline">nuestra página de rastreo</Link>.
    </p>

    <h2>6. Pedidos no recibidos o retornados</h2>
    <p>
      Si la transportadora no logra entregar (3 intentos usuales), el pedido regresa a
      nuestra bodega. Contactaremos al cliente para coordinar un segundo despacho
      (costo adicional) o el reembolso, previa deducción del envío original.
    </p>
  </LegalShell>
);

// ─────────────────────────────────────────────────────────────────
// 5) Cambios y devoluciones
// ─────────────────────────────────────────────────────────────────
export const Returns = () => (
  <LegalShell
    eyebrow="Postventa"
    title="Cambios y devoluciones"
    updated="Febrero 2026"
    icon={RotateCcw}
    testid="page-returns"
  >
    <h2>1. Derecho de retracto (Estatuto del Consumidor)</h2>
    <p>
      De acuerdo con el <b>artículo 47 de la Ley 1480 de 2011</b>, el consumidor tiene
      derecho a retractarse dentro de los <b>5 días hábiles</b> siguientes a la entrega
      del producto, siempre que:
    </p>
    <ul>
      <li>El producto no haya sido usado ni consumido y esté en su empaque original y sellado.</li>
      <li>Se conserve la factura de compra.</li>
      <li>La compra se haya realizado a través del sitio web.</li>
    </ul>
    <p>
      El valor pagado se reembolsará dentro de los <b>30 días calendario</b> siguientes
      a la aceptación del retracto. El costo del transporte de devolución corre por
      cuenta del consumidor.
    </p>

    <h2>2. Garantía por calidad e idoneidad</h2>
    <p>
      Todos nuestros productos cuentan con garantía legal según la Ley 1480 de 2011.
      Si detectas un defecto (empaque roto, producto en mal estado, error de referencia),
      contáctanos dentro de los <b>3 días hábiles</b> siguientes a la entrega adjuntando
      fotografías y número de pedido.
    </p>

    <h2>3. Cambios por preferencia de tu mascota</h2>
    <p>
      Si tu mascota no acepta el alimento después de una transición gradual (7-10 días)
      y el producto está sin abrir, puedes solicitar un cambio por otra referencia de
      igual o mayor valor. Contáctanos dentro de los <b>15 días calendario</b>
      siguientes a la entrega.
    </p>

    <h2>4. Productos no elegibles para devolución</h2>
    <ul>
      <li>Productos con empaque abierto o parcialmente consumidos (salvo defecto de calidad).</li>
      <li>Snacks, treats o productos frescos.</li>
      <li>Productos personalizados o hechos bajo pedido.</li>
    </ul>

    <h2>5. Proceso</h2>
    <ol>
      <li>Escribe a <b>{COMPANY.email}</b> o WhatsApp con tu número de pedido y motivo.</li>
      <li>Recibirás instrucciones de recolección o entrega en punto autorizado.</li>
      <li>Una vez recibido el producto en nuestra bodega, verificamos su estado.</li>
      <li>Se aprueba el cambio o el reembolso al medio de pago original.</li>
    </ol>

    <h2>6. Reembolsos</h2>
    <p>
      Los reembolsos se realizan al medio de pago original (Bold) en un plazo de <b>10 a 30
      días calendario</b> desde la aprobación, sujeto a los tiempos de la entidad emisora.
    </p>
  </LegalShell>
);
